import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContextBackend';
import { setAccessToken as setStoredAccessToken } from '../utils/tokenStorage';
import { isMainDomain, buildSubdomainUrl, getBaseDomain } from '../utils/subdomain';

/**
 * Marketplace SSO Callback — receives buyer redirect from Exchange marketplace.
 * Reads the Keycloak token from URL, stores it, calls /me, and logs the buyer in.
 * This is a separate page from AuthCallback to avoid any risk to existing login flows.
 */
export default function MarketplaceSSOCallback() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    const processSSO = async () => {
      // Prevent double execution (React StrictMode)
      if (hasProcessedRef.current) return;
      hasProcessedRef.current = true;

      // Exchange sends the token in the URL fragment (#token=...) so it is never
      // transmitted to servers/proxies/logs. Fall back to ?token= for backward compat.
      const hash = (location.hash || '').replace(/^#/, '');
      const hashParams = new URLSearchParams(hash);
      const token = hashParams.get('token') || searchParams.get('token');

      // Clear the token from the URL so it does not linger in history/back-nav.
      if (hash) {
        window.history.replaceState(null, '', location.pathname + location.search);
      }

      if (!token) {
        setError('Missing SSO token. Please try again from the Marketplace.');
        return;
      }

      // Clear the token from URL to prevent Referer leaks on subsequent navigations
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
      }

      try {
        console.log('[Marketplace SSO] Processing token...');

        // Store the token so API client can use it
        setStoredAccessToken(token);

        // Call /me API to get full user profile
        const baseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_PRIVATE_API_BASE_URL || 'https://testapps.traineryhcm.com/core/api/v1';
        const meResponse = await fetch(`${baseUrl}/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
          referrerPolicy: 'no-referrer',
        });

        if (!meResponse.ok) {
          throw new Error(`/me API returned ${meResponse.status}`);
        }

        const meData = await meResponse.json();
        console.log('[Marketplace SSO] /me response received');

        // Set auth data using existing auth context (same as normal login)
        setAuthData({
          success: true,
          access_token: token,
          refresh_token: '',  // Impersonation may not provide refresh token
          expires_in: 28800,  // 8 hours
          user: meData.user || meData,
          account: meData.account || {},
        });

        // Cross-subdomain handoff: if the account has its own subdomain (e.g. sch.testapps...)
        // and we're currently on the main domain, localStorage won't travel across origins.
        // Forward the token via URL fragment so AuthContextBackend's subdomain bootstrap
        // (see lines 311-322) can pick it up. Same pattern LoginForm uses.
        const accountDomain = (meData.account && meData.account.domain) || '';
        if (accountDomain && getBaseDomain() && isMainDomain()) {
          const target = buildSubdomainUrl(accountDomain, '/home/my-portal');
          const frag = `access_token=${encodeURIComponent(token)}`;
          console.log('[Marketplace SSO] Handing off to subdomain:', accountDomain);
          window.location.replace(`${target}#${frag}`);
          return;
        }

        console.log('[Marketplace SSO] Auth data set, redirecting to dashboard');
        navigate('/home/my-portal', { replace: true });

      } catch (err: any) {
        console.error('[Marketplace SSO] Failed:', err);
        setError('Sign-in failed. Please log in manually.');
      }
    };

    processSSO();
  }, [searchParams, location, setAuthData, navigate]);

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '16px' }}>
        <p style={{ color: '#dc2626', fontSize: '16px' }}>{error}</p>
        <button
          onClick={() => navigate('/login', { replace: true })}
          style={{ padding: '8px 24px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '12px' }}>
      <div style={{ width: '32px', height: '32px', border: '3px solid #e5e7eb', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ color: '#6b7280', fontSize: '14px' }}>Signing you in to Trainery LMS...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
